import React, { useState, useEffect } from 'react';
import { Container, Badge, Button, Form, InputGroup, Row, Col, Alert, Card, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { taskService } from '../../services/api';
import './TaskList.css';

const TrashList = () => {
  const { isAdmin } = useAuth();
  const [trashedTasks, setTrashedTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      setError('Only administrators can access the trash');
      setLoading(false);
      return;
    }

    const fetchTrashedTasks = async () => {
      try {
        setLoading(true);
        const response = await taskService.getTrashedTasks();
        // Make sure we're setting an array, even if empty
        const tasks = response.data?.data || [];
        setTrashedTasks(tasks);
        setFilteredTasks(tasks);
        setError('');
      } catch (err) {
        console.error('Error fetching trashed tasks:', err);
        setError('Failed to load trashed tasks. Please try again.');
        setTrashedTasks([]);
        setFilteredTasks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTrashedTasks();
  }, [isAdmin]);

  useEffect(() => {
    // Apply search filter
    if (!Array.isArray(trashedTasks)) {
      setFilteredTasks([]);
      return;
    }

    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      const filtered = trashedTasks.filter(task => 
        task.title.toLowerCase().includes(term) || 
        (task.description && task.description.toLowerCase().includes(term))
      );
      setFilteredTasks(filtered);
    } else {
      setFilteredTasks(trashedTasks);
    }
  }, [trashedTasks, searchTerm]);

  const handleRestore = async (taskId) => {
    try {
      await taskService.restoreFromTrash(taskId);
      // Refresh the list after restore
      const response = await taskService.getTrashedTasks();
      const tasks = response.data?.data || [];
      setTrashedTasks(tasks);
      setShowRestoreModal(false);
      setSelectedTask(null);
    } catch (err) {
      console.error('Error restoring task:', err);
      setError('Failed to restore task. Please try again.');
    }
  };

  const handlePermanentDelete = async (taskId) => {
    try {
      await taskService.permanentlyDeleteTask(taskId);
      // Refresh the list after deletion
      const response = await taskService.getTrashedTasks();
      const tasks = response.data?.data || [];
      setTrashedTasks(tasks);
      setShowDeleteModal(false);
      setSelectedTask(null);
    } catch (err) {
      console.error('Error permanently deleting task:', err);
      setError('Failed to delete task permanently. Please try again.');
    }
  };

  const confirmRestore = (task) => {
    setSelectedTask(task);
    setShowRestoreModal(true);
  };

  const confirmDelete = (task) => {
    setSelectedTask(task);
    setShowDeleteModal(true);
  };

  // Format date nicely
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString(undefined, options);
  };

  if (!isAdmin) {
    return (
      <Container fluid className="py-4">
        <Alert variant="danger">
          Only administrators can access the trash.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return <div className="loading-container"><i className="fas fa-spinner fa-spin"></i> Loading trashed tasks...</div>;
  }

  return (
    <Container fluid className="task-list-container py-4">
      <div className="tasks-header mb-4">
        <h2 className="page-title">
          <i className="fas fa-trash-alt me-2"></i>
          Trash
        </h2>
      </div>
      
      {error && <Alert variant="danger" className="mb-4">{error}</Alert>}
      
      <Card className="filter-card mb-4">
        <Card.Body>
          <Row>
            <Col md={12}>
              <div className="filter-group">
                <label className="filter-label">
                  <i className="fas fa-search me-2"></i>Search
                </label>
                <InputGroup>
                  <Form.Control
                    placeholder="Search trashed tasks..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  {searchTerm && (
                    <Button 
                      variant="outline-secondary" 
                      onClick={() => setSearchTerm('')}
                      className="clear-btn"
                    >
                      <i className="fas fa-times"></i>
                    </Button>
                  )}
                </InputGroup>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      <div className="task-stats mb-4">
        <div className="stat-pill status-trashed">
          <div className="stat-icon"><i className="fas fa-trash-alt"></i></div>
          <span>Trashed Tasks: <strong>{filteredTasks.length}</strong></span>
        </div>
      </div>

      {Array.isArray(filteredTasks) && filteredTasks.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon"><i className="fas fa-trash"></i></div>
          <h3>Trash is empty</h3>
          <p>There are no tasks in the trash</p>
        </div>
      ) : (
        <div className="task-table-container">
          <table className="task-table">
            <thead>
              <tr>
                <th>Task</th>
                <th>Deleted On</th>
                <th>Priority</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(filteredTasks) && filteredTasks.map(task => (
                <tr key={task.id} className="task-row trashed">
                  <td className="task-info">
                    <span className="task-title">{task.title}</span>
                    <div className="task-desc">{task.description?.substring(0, 80)}...</div>
                  </td>
                  <td className="task-deleted-date">
                    {formatDate(task.deleted_at)}
                  </td>
                  <td className="task-priority">
                    <Badge bg={
                      task.priority === 'low' ? 'info' :
                      task.priority === 'medium' ? 'warning' :
                      task.priority === 'high' ? 'danger' : 'danger'
                    } className="priority-badge">
                      {task.priority}
                    </Badge>
                  </td>
                  <td className="task-actions">
                    <Button 
                      variant="success" 
                      size="sm" 
                      className="me-2"
                      onClick={() => confirmRestore(task)}
                      title="Restore Task"
                    >
                      <i className="fas fa-trash-restore"></i>
                    </Button>
                    <Button 
                      variant="danger" 
                      size="sm" 
                      onClick={() => confirmDelete(task)}
                      title="Delete Permanently"
                    >
                      <i className="fas fa-times"></i>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Restore confirmation modal */}
      <Modal show={showRestoreModal} onHide={() => setShowRestoreModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Restore</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to restore "{selectedTask?.title}" from trash?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRestoreModal(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={() => handleRestore(selectedTask?.id)}>
            Restore
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Delete confirmation modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Permanent Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <strong>Warning:</strong> This action cannot be undone.
          </Alert>
          Are you sure you want to permanently delete "{selectedTask?.title}"?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => handlePermanentDelete(selectedTask?.id)}>
            Delete Permanently
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default TrashList; 